import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import type { LucideIcon } from 'lucide-react';

export type VarianteBoton = 'primario' | 'secundario' | 'peligro' | 'fantasma';
export type TamanoBoton = 'normal' | 'grande';

// Diseño Foco: el primario es el acento con su texto; el secundario, el
// acento suave con el texto principal. `sobre-marca` y `sobre-alerta` cambian
// con el tema: casi blanco en claro, verde muy oscuro en oscuro.
const VARIANTES: Record<VarianteBoton, string> = {
  primario:
    'bg-marca text-sobre-marca active:bg-marca-fuerte disabled:bg-borde disabled:text-texto-suave',
  secundario: 'bg-marca-suave text-texto active:bg-borde disabled:text-texto-suave',
  peligro:
    'bg-alerta text-sobre-alerta active:brightness-95 disabled:bg-borde disabled:text-texto-suave',
  fantasma: 'bg-transparent text-marca active:bg-marca-suave disabled:text-texto-suave',
};

const TAMANOS: Record<TamanoBoton, string> = {
  // 49 puntos de alto, como en Foco: más que el mínimo de 44 (Req. 8.1)
  normal: 'min-h-[49px] px-[15px] py-3 text-[14px]',
  // 56 puntos: los botones principales del entrenamiento (Req. 8.1)
  grande: 'min-h-14 px-5 py-3.5 text-[15px]',
};

interface OpcionesClases {
  variante?: VarianteBoton;
  tamano?: TamanoBoton;
  /** Ocupa todo el ancho disponible. Predeterminado en pantallas de teléfono. */
  completo?: boolean;
  className?: string;
}

/** Clases de un botón, para usarlas también en enlaces con aspecto de botón. */
export function clasesBoton({
  variante = 'primario',
  tamano = 'normal',
  completo = true,
  className = '',
}: OpcionesClases = {}): string {
  return [
    'inline-flex items-center justify-center gap-2.5 rounded-2xl text-center font-medium',
    'transition-colors select-none disabled:cursor-not-allowed',
    VARIANTES[variante],
    TAMANOS[tamano],
    completo ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

interface Props extends ButtonHTMLAttributes<HTMLButtonElement>, OpcionesClases {
  /** Ícono al final del texto, como en Foco. */
  icono?: LucideIcon;
  children: ReactNode;
}

/**
 * Botón base de la app (diseño Foco: radio de 16 px, texto de 14 px, ícono
 * de trazo fino a la derecha).
 *
 * El tamaño mínimo no es decorativo: cumple el área táctil del Req. 8.1,
 * pensada para usarse con el pulgar entre series.
 *
 * Origen: Control de Negocio de Comida, con el estilo de Foco.
 */
export function Button({
  variante,
  tamano,
  completo,
  className,
  icono: Icono,
  type = 'button',
  children,
  ...resto
}: Props) {
  return (
    <button
      type={type}
      className={clasesBoton({
        variante,
        tamano,
        completo,
        ...(className !== undefined ? { className } : {}),
      })}
      {...resto}
    >
      {children}
      {Icono !== undefined && <Icono className="size-[18px]" />}
    </button>
  );
}

interface PropsEnlace extends OpcionesClases {
  to: string;
  icono?: LucideIcon;
  /** Reemplaza esta pantalla en el historial: "atrás" no regresa a ella. */
  reemplazar?: boolean;
  children: ReactNode;
}

/**
 * Enlace con aspecto de botón. Evita anidar un <button> dentro de un <a>,
 * que no es HTML válido y confunde a los lectores de pantalla.
 */
export function BotonEnlace({ to, icono: Icono, reemplazar = false, children, ...opciones }: PropsEnlace) {
  return (
    <Link to={to} replace={reemplazar} className={clasesBoton(opciones)}>
      {children}
      {Icono !== undefined && <Icono className="size-[18px]" />}
    </Link>
  );
}
